import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  Users,
  Building2,
  Mail,
  Phone,
  X,
  AlertTriangle,
  Eye,
  Package,
  RefreshCw,
  Filter,
} from 'lucide-react'
import { customerService } from '../services/customerService'
import type { Customer, CustomerRequest, CustomerSearchParams } from '../types/customer'
import AssetContainerModal from './AssetContainerModal'

const PAGE_SIZE = 20

function unwrapCustomerList(res: unknown): Customer[] {
  if (!res || typeof res !== 'object') return []
  const o = res as Record<string, unknown>
  if (Array.isArray(o.data)) return o.data as Customer[]
  if (Array.isArray(o.content)) return o.content as Customer[]
  if (Array.isArray(res)) return res as Customer[]
  return []
}

function unwrapTotal(res: unknown, fallback: number): number {
  if (!res || typeof res !== 'object') return fallback
  const o = res as Record<string, unknown>
  if (typeof o.total === 'number') return o.total
  if (typeof o.totalElements === 'number') return o.totalElements
  if (typeof o.totalCount === 'number') return o.totalCount
  return fallback
}

function formatDateInput(iso?: string): string {
  if (!iso) return ''
  const d = iso.slice(0, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : ''
}

const CUSTOMER_TYPES: Customer['customer_type'][] = ['SHIPPING', 'LOGISTICS', 'RETAIL', 'INDUSTRIAL']

const emptyForm: CustomerRequest = {
  customer_code: '',
  company_name: '',
  contact_person: '',
  contact_email: '',
  contact_phone: '',
  address: '',
  country: '',
  customer_type: 'SHIPPING',
  contract_start_date: '',
  contract_end_date: '',
  notes: '',
}

export interface CustomerManagementProps {
  showMessage: (msg: string, type: 'success' | 'error' | 'warning' | 'info') => void
}

const CustomerManagement: React.FC<CustomerManagementProps> = ({ showMessage }) => {
  const { t } = useTranslation()

  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalElements, setTotalElements] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [assetFilter, setAssetFilter] = useState('')
  const [containerFilter, setContainerFilter] = useState('')
  const [sortBy, setSortBy] = useState('company_name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const [modalOpen, setModalOpen] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null)
  const [formData, setFormData] = useState<CustomerRequest>(emptyForm)
  const [saving, setSaving] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState<Customer | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')

  const [detailCustomer, setDetailCustomer] = useState<Customer | null>(null)
  const [acmCustomer, setAcmCustomer] = useState<Customer | null>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  useEffect(() => { setPage(1) }, [debouncedSearch])

  const fetchCustomers = useCallback(async () => {
    setLoading(true)
    try {
      const params: CustomerSearchParams = {
        page,
        size: PAGE_SIZE,
        search: debouncedSearch || undefined,
        sortBy,
        sortOrder,
      }
      const res = await customerService.getCustomers(params)
      const list = unwrapCustomerList(res)
      const total = unwrapTotal(res, list.length)
      setCustomers(list)
      setTotalElements(total)
    } catch (e) {
      console.error(e)
      showMessage(t('customerMgmt.loadFailed', { defaultValue: 'Failed to load customers' }), 'error')
      setCustomers([])
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, page, sortBy, sortOrder, showMessage, t])

  useEffect(() => { void fetchCustomers() }, [fetchCustomers])

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalElements / PAGE_SIZE) || 1), [totalElements])

  const filteredCustomers = useMemo(() => {
    let list = customers
    if (assetFilter) {
      list = list.filter((c) => {
        const total = (c.active_assets || 0) + (c.inactive_assets || 0)
        if (assetFilter === 'with_assets') return total > 0
        if (assetFilter === 'without_assets') return total === 0
        if (assetFilter === 'active_assets') return (c.active_assets || 0) > 0
        return true
      })
    }
    if (containerFilter) {
      list = list.filter((c) => {
        const total = (c.active_containers || 0) + (c.inactive_containers || 0)
        if (containerFilter === 'with_containers') return total > 0
        if (containerFilter === 'without_containers') return total === 0
        if (containerFilter === 'active_containers') return (c.active_containers || 0) > 0
        return true
      })
    }
    return list
  }, [customers, assetFilter, containerFilter])

  const totalCustomers = totalElements
  // The API sends contract_status (there is no `active` boolean) — counting
  // on `c.active` made this stat permanently 0.
  const isActiveCustomer = (c: Customer) =>
    String(c.contract_status || '').toUpperCase() === 'ACTIVE' || c.active === true
  const activeCustomers = filteredCustomers.filter(isActiveCustomer).length

  const openCreate = () => {
    setEditingCustomer(null)
    setFormData({ ...emptyForm })
    setModalOpen(true)
  }

  const openEdit = (c: Customer) => {
    setEditingCustomer(c)
    setFormData({
      customer_code: c.customer_code,
      company_name: c.company_name,
      contact_person: c.contact_person ?? '',
      contact_email: c.contact_email ?? '',
      contact_phone: c.contact_phone ?? '',
      address: c.address ?? '',
      country: c.country ?? '',
      customer_type: c.customer_type,
      contract_start_date: formatDateInput(c.contract_start_date),
      contract_end_date: formatDateInput(c.contract_end_date),
      notes: c.notes ?? '',
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingCustomer(null)
    setFormData({ ...emptyForm })
  }

  const handleFormChange = (field: keyof CustomerRequest, value: string) => {
    if (field === 'customer_code') {
      setFormData((prev) => ({ ...prev, customer_code: value.toUpperCase() }))
      return
    }
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const validateForm = (): boolean => {
    if (!formData.customer_code.trim()) {
      showMessage(t('customerMgmt.codeRequired', { defaultValue: 'Customer code is required' }), 'warning')
      return false
    }
    if (!formData.company_name.trim()) {
      showMessage(t('customerMgmt.companyRequired', { defaultValue: 'Company name is required' }), 'warning')
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm()) return
    setSaving(true)
    try {
      if (editingCustomer) {
        await customerService.updateCustomer(editingCustomer.customer_code, formData)
        showMessage(t('customerMgmt.saved', { defaultValue: 'Customer updated' }), 'success')
      } else {
        await customerService.createCustomer(formData)
        showMessage(t('customerMgmt.created', { defaultValue: 'Customer created' }), 'success')
      }
      closeModal()
      await fetchCustomers()
    } catch (e) {
      console.error(e)
      showMessage(t('customerMgmt.saveFailed', { defaultValue: 'Could not save customer' }), 'error')
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    if (deleteConfirmText.trim() !== deleteTarget.company_name.trim()) {
      showMessage(t('customerMgmt.nameNoMatch', { defaultValue: 'Name does not match' }), 'error')
      return
    }
    setDeleting(true)
    try {
      await customerService.deleteCustomer(String(deleteTarget.id))
      showMessage(t('customerMgmt.deleted', { defaultValue: 'Customer deleted' }), 'success')
      setDeleteTarget(null)
      setDeleteConfirmText('')
      await fetchCustomers()
    } catch (e) {
      console.error(e)
      showMessage(t('customerMgmt.deleteFailed', { defaultValue: 'Could not delete customer' }), 'error')
    } finally {
      setDeleting(false)
    }
  }

  const clearFilters = () => {
    setSearchInput('')
    setAssetFilter('')
    setContainerFilter('')
    setPage(1)
  }

  const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <div className="h-full bg-gray-50 flex flex-col overflow-hidden">
      {/* Stats Header - matching BM layout */}
      <div className="shrink-0 bg-white border-b border-gray-200">
        <div className="flex items-center justify-center gap-16 py-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-900">{totalCustomers}</p>
            <p className="text-sm text-gray-500">{t('customerMgmt.statTotal', { defaultValue: 'Total Customers' })}</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-900">{activeCustomers}</p>
            <p className="text-sm text-gray-500">{t('customerMgmt.statActive', { defaultValue: 'Active Customers' })}</p>
          </div>
        </div>
      </div>

      {/* Filters - matching BM Customers page */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1 min-w-0 max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('customerMgmt.searchPlaceholder', { defaultValue: 'Search customers…' })}
              className={`${inputClass} pl-10`} />
          </div>
          <select value={assetFilter} onChange={(e) => setAssetFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white min-w-[150px]">
            <option value="">{t('customerMgmt.assetFilterAll', { defaultValue: 'Assets: all' })}</option>
            <option value="with_assets">{t('customerMgmt.withAssets', { defaultValue: 'With Assets' })}</option>
            <option value="without_assets">{t('customerMgmt.withoutAssets', { defaultValue: 'Without Assets' })}</option>
            <option value="active_assets">{t('customerMgmt.activeAssetsOnly', { defaultValue: 'With Active Assets' })}</option>
          </select>
          <select value={containerFilter} onChange={(e) => setContainerFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white min-w-[150px]">
            <option value="">{t('customerMgmt.containerFilterAll', { defaultValue: 'Containers: all' })}</option>
            <option value="with_containers">{t('customerMgmt.withContainers', { defaultValue: 'With Containers' })}</option>
            <option value="without_containers">{t('customerMgmt.withoutContainers', { defaultValue: 'Without Containers' })}</option>
            <option value="active_containers">{t('customerMgmt.activeContainersOnly', { defaultValue: 'With Active Containers' })}</option>
          </select>
          <button type="button" onClick={clearFilters}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium">
            <Filter className="w-4 h-4" /> {t('clear', { defaultValue: 'CLEAR' })}
          </button>
          <button type="button" onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">
            <Plus className="w-4 h-4" /> {t('customerMgmt.addCustomer', { defaultValue: '+ ADD CUSTOMER' })}
          </button>
        </div>
      </div>

      {/* Table - matching BM Customers page: Company, Contact, Assets, Containers, Balises, Actions */}
      <div className="min-h-0 flex-1 overflow-hidden px-4 pb-2 pt-3">
        <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="min-w-full border-collapse text-left text-sm" style={{ minWidth: '900px' }}>
              <thead className="sticky top-0 z-10 bg-blue-600 text-white shadow-sm">
                <tr>
                  <th className="px-4 py-3 text-sm font-semibold" style={{ minWidth: '200px' }}>{t('company')}</th>
                  <th className="px-4 py-3 text-sm font-semibold" style={{ minWidth: '180px' }}>{t('customerMgmt.contact', { defaultValue: 'Contact' })}</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold" style={{ minWidth: '120px' }}>{t('customerMgmt.assets', { defaultValue: 'Assets' })}</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold" style={{ minWidth: '120px' }}>{t('customerMgmt.containers', { defaultValue: 'Containers' })}</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold" style={{ minWidth: '80px' }}>{t('customerMgmt.balises', { defaultValue: 'Balises' })}</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold" style={{ minWidth: '140px' }}>{t('actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-16 text-center text-gray-500">{t('loading')}</td></tr>
                ) : filteredCustomers.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-16 text-center text-gray-500">{t('no_results')}</td></tr>
                ) : (
                  filteredCustomers.map((c, index) => (
                    <tr key={c.id} className={`hover:bg-gray-50/80 ${index % 2 !== 0 ? 'bg-gray-50/30' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{c.company_name}</div>
                        <div className="text-xs text-gray-500">{c.customer_code}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{c.contact_person || t('customerMgmt.noContact', { defaultValue: 'No contact' })}</div>
                        <div className="text-xs text-gray-500">{c.contact_email || t('customerMgmt.noEmail', { defaultValue: 'No email' })}</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Active: {c.active_assets || 0}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                            Inactive: {c.inactive_assets || 0}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Active: {c.active_containers || 0}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                            Inactive: {c.inactive_containers || 0}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-sm font-bold">
                          {c.actual_device_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button type="button" onClick={() => setDetailCustomer(c)} title={t('customerMgmt.viewDetails', { defaultValue: 'View details' })}
                            className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50"><Eye className="w-4 h-4" /></button>
                          <button type="button" onClick={() => setAcmCustomer(c)} title={t('customerMgmt.manageAssetsContainers', { defaultValue: 'Assets & Containers' })}
                            className="p-1.5 rounded-lg text-purple-600 hover:bg-purple-50"><Package className="w-4 h-4" /></button>
                          <button type="button" onClick={() => openEdit(c)} title={t('edit')}
                            className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100"><Edit2 className="w-4 h-4" /></button>
                          <button type="button" onClick={() => { setDeleteTarget(c); setDeleteConfirmText('') }} title={t('delete')}
                            className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex shrink-0 items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-600">
            <span>{t('customerMgmt.showingOf', { defaultValue: 'Showing {{shown}} of {{total}} customers', shown: filteredCustomers.length, total: totalElements })}</span>
            <div className="flex gap-2">
              <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">{t('back')}</button>
              <button type="button" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">{t('next')}</button>
            </div>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {detailCustomer && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">{detailCustomer.company_name}</h2>
              <button type="button" onClick={() => setDetailCustomer(null)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <dl className="space-y-3 text-sm">
                {[
                  [t('customerMgmt.customerCode', { defaultValue: 'Code' }), detailCustomer.customer_code],
                  [t('type'), detailCustomer.customer_type],
                  [t('customerMgmt.contactPerson', { defaultValue: 'Contact' }), detailCustomer.contact_person || '—'],
                  [t('email'), detailCustomer.contact_email || '—'],
                  [t('phone'), detailCustomer.contact_phone || '—'],
                  [t('customerMgmt.address', { defaultValue: 'Address' }), detailCustomer.address || '—'],
                  [t('customerMgmt.country', { defaultValue: 'Country' }), detailCustomer.country || '—'],
                  [t('customerMgmt.assets', { defaultValue: 'Assets' }), `Active: ${detailCustomer.active_assets || 0}, Inactive: ${detailCustomer.inactive_assets || 0}`],
                  [t('customerMgmt.containers', { defaultValue: 'Containers' }), `Active: ${detailCustomer.active_containers || 0}, Inactive: ${detailCustomer.inactive_containers || 0}`],
                  [t('customerMgmt.balises', { defaultValue: 'Balises' }), String(detailCustomer.actual_device_count || 0)],
                  [t('status'), isActiveCustomer(detailCustomer) ? t('active') : (detailCustomer.contract_status || t('inactive'))],
                ].map(([label, value], i) => (
                  <div key={i} className="flex justify-between gap-4"><dt className="text-gray-500">{label}</dt><dd className="font-medium text-gray-900 text-right">{value}</dd></div>
                ))}
              </dl>
              {detailCustomer.notes && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">{t('notes')}</p>
                  <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{detailCustomer.notes}</p>
                </div>
              )}
            </div>
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
              <button type="button" onClick={() => setDetailCustomer(null)} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">{t('close')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl" role="dialog" aria-modal="true">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingCustomer ? t('customerMgmt.editTitle', { defaultValue: 'Edit Customer' }) : t('customerMgmt.createTitle', { defaultValue: 'New Customer' })}
                </h2>
              </div>
              <button type="button" onClick={closeModal} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4 p-6">
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">{t('customerMgmt.customerCode', { defaultValue: 'Customer code' })} *</label>
                <input className={inputClass} value={formData.customer_code} onChange={(e) => handleFormChange('customer_code', e.target.value)} disabled={!!editingCustomer} autoComplete="off" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">{t('company')} *</label>
                <input className={inputClass} value={formData.company_name} onChange={(e) => handleFormChange('company_name', e.target.value)} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">{t('customerMgmt.contactPerson', { defaultValue: 'Contact person' })}</label>
                <input className={inputClass} value={formData.contact_person ?? ''} onChange={(e) => handleFormChange('contact_person', e.target.value)} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">{t('email')}</label>
                <input type="email" className={inputClass} value={formData.contact_email ?? ''} onChange={(e) => handleFormChange('contact_email', e.target.value)} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">{t('phone')}</label>
                <input className={inputClass} value={formData.contact_phone ?? ''} onChange={(e) => handleFormChange('contact_phone', e.target.value)} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">{t('type')}</label>
                <select className={inputClass} value={formData.customer_type} onChange={(e) => handleFormChange('customer_type', e.target.value as Customer['customer_type'])}>
                  {CUSTOMER_TYPES.map((ct) => <option key={ct} value={ct}>{ct}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">{t('customerMgmt.address', { defaultValue: 'Address' })}</label>
                <input className={inputClass} value={formData.address ?? ''} onChange={(e) => handleFormChange('address', e.target.value)} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">{t('customerMgmt.country', { defaultValue: 'Country' })}</label>
                <input className={inputClass} value={formData.country ?? ''} onChange={(e) => handleFormChange('country', e.target.value)} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">{t('customerMgmt.contractStart', { defaultValue: 'Contract start' })}</label>
                <input type="date" className={inputClass} value={formatDateInput(formData.contract_start_date)} onChange={(e) => handleFormChange('contract_start_date', e.target.value)} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">{t('customerMgmt.contractEnd', { defaultValue: 'Contract end' })}</label>
                <input type="date" className={inputClass} value={formatDateInput(formData.contract_end_date)} onChange={(e) => handleFormChange('contract_end_date', e.target.value)} />
              </div>
              <div className="col-span-2">
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">{t('notes')}</label>
                <textarea rows={3} className={inputClass} value={formData.notes ?? ''} onChange={(e) => handleFormChange('notes', e.target.value)} />
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-4">
              <button type="button" onClick={closeModal} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">{t('cancel')}</button>
              <button type="button" disabled={saving} onClick={() => void handleSave()} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                {saving ? t('loading') : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{t('customerMgmt.deleteTitle', { defaultValue: 'Delete Customer' })}</h3>
                <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="font-semibold text-gray-900">{deleteTarget.company_name}</div>
                  <div className="text-xs text-gray-500 mt-1">Code: {deleteTarget.customer_code}</div>
                  <div className="text-xs text-gray-500">Balises: {deleteTarget.actual_device_count || 0} | Containers: {deleteTarget.active_containers || 0} | Assets: {deleteTarget.active_assets || 0}</div>
                </div>
                {((deleteTarget.actual_device_count || 0) > 0 || (deleteTarget.active_containers || 0) > 0 || (deleteTarget.active_assets || 0) > 0) && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                    {t('customerMgmt.cannotDeleteAssociations', { defaultValue: 'Warning: This customer has associated records. Remove them first.' })}
                  </div>
                )}
                <div className="mt-3">
                  <label className="block text-sm text-gray-700 mb-1">{t('customerMgmt.typeToConfirm', { defaultValue: 'Type the company name to confirm:' })}</label>
                  <input type="text" value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder={deleteTarget.company_name} autoComplete="off" />
                  {deleteConfirmText && (
                    <p className={`mt-1 text-xs ${deleteConfirmText.trim() === deleteTarget.company_name.trim() ? 'text-green-600' : 'text-red-600'}`}>
                      {deleteConfirmText.trim() === deleteTarget.company_name.trim() ? t('customerMgmt.nameMatch', { defaultValue: 'Name matches - you can proceed' }) : t('customerMgmt.nameNoMatch', { defaultValue: 'Name does not match' })}
                    </p>
                  )}
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <button type="button" disabled={deleting} onClick={() => { setDeleteTarget(null); setDeleteConfirmText('') }}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">{t('cancel')}</button>
                  <button type="button" disabled={deleting || deleteConfirmText.trim() !== deleteTarget.company_name.trim()} onClick={() => void confirmDelete()}
                    className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">{deleting ? t('loading') : t('delete')}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <AssetContainerModal
        isOpen={!!acmCustomer}
        onClose={() => setAcmCustomer(null)}
        customer={acmCustomer}
        showMessage={showMessage}
      />
    </div>
  )
}

export default CustomerManagement
